import { Button, Card, Collapse, Flex, Modal } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useCreateSectionMutation,
  useDeleteSectionMutation,
  usePublicCourseDetailsQuery,
  useUpdateSectionMutation,
} from "../../../app/features/adminContent/adminContent.api";
import type {
  SectionDto,
  UpdateSectionDto,
} from "../../../app/features/adminContent/adminContent.types";
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import PlusIcon from "../../../assets/icons/PlusIcon";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import LectureComponent from "./_components/LectureComponent";
import LectureList from "./_components/LectureList";
import SectionModal from "./_components/SectionModal";
import TitleProgressbar from "./_components/TitleProgressbar";

const { Panel } = Collapse;

export default function ManageContent() {
  const { courseId } = useParams();
  const courseIdNum = courseId ? Number(courseId) : undefined;
  const navigate = useNavigate();

  // const { data: sections, isLoading } = useGetSectionsByCourseIdQuery(
  //   { courseId: courseIdNum as number },
  //   { skip: !courseIdNum },
  // );
  const { data: courseDetails, isLoading } = usePublicCourseDetailsQuery(
    Number(courseId),
    {
      skip: !courseId,
    },
  );

  const sectionList = courseDetails?.CourseSection || [];

  const [createSection, { isLoading: isCreating }] = useCreateSectionMutation();
  const [updateSection, { isLoading: isUpdating }] = useUpdateSectionMutation();
  const [deleteSection] = useDeleteSectionMutation();

  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionSortOrder, setSectionSortOrder] = useState<number | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "update">("create");
  const [selectedSection, setSelectedSection] = useState<number | null>(null);

  const handleCreateSection = () => {
    setModalMode("create");
    setModalMode("create");
    setSelectedSection(null);
    setSectionTitle("");
    setSectionSortOrder(undefined);
    setIsModalOpen(true);
  };

  const handleUpdateSection = (
    sectionId: number,
    currentTitle: string,
    currentSortOrder: number,
  ) => {
    setModalMode("update");
    setSelectedSection(sectionId);
    setSectionTitle(currentTitle);
    setSectionSortOrder(currentSortOrder);
    setIsModalOpen(true);
  };

  const handleDeleteSection = (sectionId: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this section?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteSection(sectionId).unwrap();
          toast("success", "Section deleted successfully");
        } catch (error: any) {
          toast("error", error?.data?.message || "Failed to delete section");
        }
      },
    });
  };

  const handleModalSubmit = async (values: {
    title: string;
    sortOrder?: number;
  }) => {
    if (!courseIdNum) return;

    try {
      if (modalMode === "create") {
        const sectionData: SectionDto = {
          courseId: courseIdNum,
          title: values.title,
          sortOrder: values.sortOrder ?? sectionList.length,
          duration: 0,
          lectureCount: 0,
        };
        await createSection(sectionData).unwrap();
        toast("success", "Section created successfully");
      } else if (modalMode === "update" && selectedSection) {
        const updateData: UpdateSectionDto = {
          title: values.title,
          sortOrder: values.sortOrder,
        };
        await updateSection({ id: selectedSection, body: updateData }).unwrap();
        toast("success", "Section updated successfully");
      }
      setIsModalOpen(false);
      setSelectedSection(null);
      setSectionTitle("");
    } catch (error: any) {
      toast("error", error?.data?.message || "Failed to save section");
    }
  };

  return (
    <DashboardLayout title="Dashboard">
      <TitleProgressbar step={2} />

      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8 text-gray-400">
            Loading sections...
          </div>
        ) : sectionList && sectionList?.length > 0 ? (
          sectionList?.map((section) => (
            <Card
              key={section?.id}
              className="rounded-md shadow-sm"
              bodyStyle={{ padding: "0px" }}
            >
              <Collapse
                bordered={false}
                expandIconPosition="end"
                expandIcon={({ isActive }) => (
                  <ChevronDownIcon
                    className={`w-5 h-5 text-[#8897AE] transition-transform ${
                      isActive ? "rotate-180" : ""
                    }`}
                  />
                )}
                className="bg-white"
              >
                <Panel
                  key={section.id}
                  header={
                    <Flex
                      justify="space-between"
                      align="center"
                      className="w-full pr-4"
                    >
                      <span className="text-slate-700 text-lg font-medium">
                        {section.title || "Untitled Section"}
                      </span>
                      <Flex
                        gap={12}
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Flex gap={8} align="center">
                          <span className="text-slate-500 text-sm">
                            {section?.Lecture?.length || 0} Lecture
                            {(section?.Lecture?.length || 0) > 1 ? "s" : ""}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-500 text-sm">
                            {section?.Quiz?.length || 0} Quiz
                            {(section?.Quiz?.length || 0) > 1 ? "zes" : ""}
                          </span>
                        </Flex>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditIcon className="text-[#3c8331]" />}
                          onClick={() =>
                            handleUpdateSection(
                              section.id,
                              section.title,
                              section.sortOrder,
                            )
                          }
                        />
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteIcon className="text-[#FF3838]" />}
                          onClick={() => handleDeleteSection(section.id)}
                          className="px-2"
                        />
                      </Flex>
                    </Flex>
                  }
                >
                  <div className="space-y-3">
                    <LectureList
                      list={section?.Lecture}
                      sectionId={section.id}
                      courseId={courseIdNum!}
                    />
                    <LectureComponent
                      sectionId={section.id}
                      courseId={courseIdNum!}
                    />
                  </div>
                </Panel>
              </Collapse>
            </Card>
          ))
        ) : (
          <Card className="rounded-md shadow-sm">
            <div className="text-center py-8 text-gray-400">
              No sections yet. Create your first section to get started.
            </div>
          </Card>
        )}

        <Button
          type="default"
          size="middle"
          className="btn-bordered-primary border border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200 mt-4"
          onClick={handleCreateSection}
          icon={<PlusIcon className="text-lg" />}
        >
          Add New Section
        </Button>
      </div>

      <Flex justify="space-between" align="center" className="mt-6">
        <Button
          type="default"
          className="btn-bordered-primary border border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200 min-w-[100px]"
          onClick={() =>
            navigate(`/admin/my-course/manage-course/highlights/${courseIdNum}`)
          }
        >
          Back
        </Button>
        {/* <Button
          type="primary"
          className="min-w-[100px]"
          onClick={() => navigate(`/admin/dashboard`)}
        >
          Continue
        </Button> */}
      </Flex>

      <SectionModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedSection(null);
          setSectionTitle("");
        }}
        onSubmit={handleModalSubmit}
        mode={modalMode}
        initialTitle={sectionTitle}
        initialSortOrder={
          modalMode === "create" ? sectionList.length : sectionSortOrder
        }
        loading={isCreating || isUpdating}
      />
    </DashboardLayout>
  );
}
